import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTranslation } from 'react-i18next';

const { FiMail, FiPhone, FiMapPin, FiInstagram, FiFacebook, FiSend } = FiIcons;

const Contact = () => {
  const [showForm, setShowForm] = useState(false);
  const { t } = useTranslation();

  // Load the script once the component is mounted
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://link.msgsndr.com/js/form_embed.js";
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const contactInfo = [
    {
      icon: FiMail,
      title: t('Email'),
      content: 'elviraviglianopm@gmail.com',
      link: 'mailto:elviraviglianopm@gmail.com'
    },
    {
      icon: FiPhone,
      title: t('Telefono'),
      content: '+39 377 045 7022',
      link: 'tel:+393770457022'
    },
    {
      icon: FiMapPin,
      title: t('Servizio'),
      content: t('Tutta Italia'),
      link: null
    }
  ];

  const socialLinks = [
    {
      icon: FiInstagram,
      name: 'Instagram',
      link: 'https://www.instagram.com/elvira_propertymanager/#'
    },
    {
      icon: FiFacebook,
      name: 'Facebook',
      link: 'https://www.facebook.com/profile.php?id=61577142384907'
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-warm-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752009564982-photo_5999181830990974370_w.jpg" 
                alt="Elvira Vigliano Property Manager" 
                className="h-24 w-24 rounded-full border-4 border-primary-200 shadow-lg" 
              />
            </div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold text-warm-900 mb-6"
            >
              {t('Contattaci')}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-warm-700 mb-8 max-w-3xl mx-auto"
            >
              {t('Siamo qui per rispondere a tutte le tue domande e aiutarti a massimizzare i guadagni del tuo immobile')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6 }} 
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-warm-900 mb-8">{t('Informazioni di Contatto')}</h2>
              <div className="space-y-6 mb-8">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="bg-primary-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={info.icon} className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-warm-900 mb-1">{info.title}</h3>
                      {info.link ? (
                        <a 
                          href={info.link} 
                          className="text-warm-600 hover:text-primary-600 transition-colors"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-warm-600">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-warm-900 mb-4">{t('Seguici sui Social')}</h3>
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <a 
                      key={index} 
                      href={social.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="bg-warm-100 w-12 h-12 rounded-lg flex items-center justify-center hover:bg-primary-600 hover:text-white transition-colors duration-200" 
                      aria-label={social.name}
                    >
                      <SafeIcon icon={social.icon} className="h-6 w-6" />
                    </a>
                  ))}
                </div>
              </div>
              <div className="bg-primary-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-warm-900 mb-2">{t('Proprietaria')}</h3>
                <div className="flex items-center space-x-4">
                  <img 
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752009564982-photo_5999181830990974370_w.jpg" 
                    alt="Elvira Vigliano" 
                    className="h-16 w-16 rounded-full border-2 border-primary-200" 
                  />
                  <p className="text-warm-700">Elvira Vigliano – {t('Property Manager')}</p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }} 
              viewport={{ once: true }}
            >
              <div className="bg-warm-50 p-8 rounded-lg shadow-lg h-full">
                <h2 className="text-3xl font-bold text-warm-900 mb-6">{t('Invia un Messaggio')}</h2>
                
                {/* Toggle Button */}
                <div className="mb-6">
                  <motion.button 
                    onClick={() => setShowForm(!showForm)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-primary-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary-700 transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiSend} className="h-5 w-5" />
                    <span>{t('Manda il messaggio')}</span>
                  </motion.button>
                </div>

                {/* Lead Connector Form */}
                {showForm && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }} 
                    animate={{ opacity: 1, height: "602px" }} 
                    exit={{ opacity: 0, height: 0 }} 
                    transition={{ duration: 0.5 }} 
                    className="form-container overflow-hidden" 
                    style={{ position: "relative" }}
                  >
                    <iframe 
                      src="https://api.leadconnectorhq.com/widget/form/VKsHPWBSG5R6J3DyrAEf" 
                      style={{ width: "100%", height: "100%", border: "none", borderRadius: "3px" }} 
                      id="popup-VKsHPWBSG5R6J3DyrAEf" 
                      data-layout="{'id':'POPUP'}" 
                      data-trigger-type="alwaysShow" 
                      data-trigger-value="" 
                      data-activation-type="alwaysActivated" 
                      data-activation-value="" 
                      data-deactivation-type="neverDeactivate" 
                      data-deactivation-value="" 
                      data-form-name="Form 7" 
                      data-height="602" 
                      data-layout-iframe-id="popup-VKsHPWBSG5R6J3DyrAEf" 
                      data-form-id="VKsHPWBSG5R6J3DyrAEf" 
                      title="Form 7"
                    ></iframe>
                  </motion.div>
                )}

                {!showForm && (
                  <div className="text-center py-12">
                    <p className="text-warm-600 text-lg">
                      {t('Clicca il pulsante sopra per aprire il modulo di contatto')}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Properties Images */}
      <section className="py-12 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-warm-900 mb-4">{t('I Nostri Immobili in Gestione')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }} 
              viewport={{ once: true }}
              className="rounded-lg overflow-hidden shadow-lg"
            >
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753287373694-blob" 
                alt="Villa frontemare Monterosso" 
                className="w-full h-48 object-cover" 
              />
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.1 }} 
              viewport={{ once: true }}
              className="rounded-lg overflow-hidden shadow-lg"
            >
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753287967247-oleandri02-461242530.jpg" 
                alt="Residence Gli Oleandri" 
                className="w-full h-48 object-cover" 
              />
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }} 
              viewport={{ once: true }}
              className="rounded-lg overflow-hidden shadow-lg"
            >
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753288194892-Loft-in-vendita-milano-608858958.jpg" 
                alt="Loft Design Milano" 
                className="w-full h-48 object-cover" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('Richiedi una Consulenza Gratuita')}</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              {t('Scopri quanto puoi guadagnare con il tuo immobile. La consulenza è completamente gratuita e senza impegno.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:elviraviglianopm@gmail.com" 
                className="inline-block bg-white text-primary-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-warm-50 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {t('Contattaci via Email')}
              </a>
              <a 
                href="tel:+393770457022" 
                className="inline-block bg-primary-700 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary-800 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {t('Chiamaci Ora')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;